import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import * as persisted from '#/state/persisted'
import {flagWithDefault} from '#/state/persisted/schema'

type StateContext = boolean
type SetContext = (v: boolean) => void

/** The stored choice, or the default when the reader has never made one. */
function read(): boolean {
  return flagWithDefault(
    persisted.get('flashbookSoundEffects'),
    persisted.defaults.flashbookSoundEffects,
  )
}

const stateContext = createContext<StateContext>(read())
stateContext.displayName = 'FlashBookSoundEffectsStateContext'
const setContext = createContext<SetContext>((_: boolean) => {})
setContext.displayName = 'FlashBookSoundEffectsSetContext'

export function Provider({children}: {children: React.ReactNode}) {
  const [state, setState] = useState(read)

  const setStateWrapped = useCallback(
    (enabled: persisted.Schema['flashbookSoundEffects']) => {
      setState(Boolean(enabled))
      persisted.write('flashbookSoundEffects', enabled)
    },
    [setState],
  )

  useEffect(() => {
    return persisted.onUpdate(
      'flashbookSoundEffects',
      nextFlashbookSoundEffects => {
        setState(nextFlashbookSoundEffects !== false)
      },
    )
  }, [setStateWrapped])

  return (
    <stateContext.Provider value={state}>
      <setContext.Provider value={setStateWrapped}>
        {children}
      </setContext.Provider>
    </stateContext.Provider>
  )
}

/**
 * Whether FlashBook readers play interaction sounds.
 *
 * Separate from the app's general mute (which governs music and video): a
 * reader may want a quiet book — a classroom, a bedtime story — while still
 * hearing the rest of the app, or the other way round. Device-wide.
 */
export const useFlashBookSoundEffects = () => useContext(stateContext)
export const useSetFlashBookSoundEffects = () => useContext(setContext)
